import { useCallback, useEffect, useRef, useState } from 'react';
import type { VehicleCamera } from '../types/vehicle';
import { getVehicle } from '../lib/api/endpoints';
import { vehicleFromDto } from '../lib/api/mappers';
import { ApiError } from '../lib/api/client';

// FE-14: `Vehicle.cameras`/`hasLiveVideo` solo vienen poblados en
// `GET /vehicles/{id}` (el detalle) — el listado los omite a propósito para
// evitar un N+1 (openapi.yaml). Este hook pide el detalle bajo demanda, solo
// cuando una pantalla de detalle lo necesita, nunca por cada fila de una lista.
export function useVehicleCameras(vehicleId: string | null) {
  const [cameras, setCameras] = useState<VehicleCamera[]>([]);
  const [hasLiveVideo, setHasLiveVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    if (!vehicleId) {
      setCameras([]);
      setHasLiveVideo(false);
      return;
    }
    setIsLoading(true);
    try {
      const dto = await getVehicle(vehicleId);
      if (cancelledRef.current) return;
      const vehicle = vehicleFromDto(dto);
      setCameras(vehicle.cameras ?? []);
      setHasLiveVideo(vehicle.hasLiveVideo ?? false);
      setError(null);
    } catch (err) {
      if (!cancelledRef.current) {
        setCameras([]);
        setHasLiveVideo(false);
        // Sin sesión real (loginDev, FE-01) el cliente lanza antes de llegar
        // a la red — se trata igual que cualquier error: sin cámaras, no
        // una pantalla rota (mismo criterio que useVehicles.ts).
        setError(
          err instanceof ApiError || err instanceof Error
            ? err
            : new Error('Error desconocido cargando cámaras del vehículo'),
        );
      }
    } finally {
      if (!cancelledRef.current) setIsLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  return { cameras, hasLiveVideo, isLoading, error };
}
